import React, { useEffect, useMemo, useState } from "react";
import { Package2, Plus, Trash2, Info } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const CONSUMABLE_TYPE_LABELS = {
  barquette: "Barquette",
  film: "Film",
  gaz: "Gaz",
  etiquette: "Étiquette",
  consommable_imprimante: "Encre/cartouche",
  autre: "Autre",
};

/**
 * Éditeur des consommables emballage liés à un produit.
 *
 * Champ `packaging` (array of {consumable_id, quantity_per_unit, unit}).
 *
 * Le coût packaging calculé est récupéré depuis cost-breakdown et affiché à droite.
 * Invisible : cette donnée n'apparaît PAS sur les étiquettes RGPD ni dans les vues cuisine.
 *
 * Usage :
 *   <ProductPackagingEditor
 *     packaging={form.packaging}
 *     onChange={(next) => setForm({...form, packaging: next})}
 *     breakdownPackaging={costBreakdown?.packaging}
 *   />
 */
export default function ProductPackagingEditor({ packaging, onChange, breakdownPackaging }) {
  const [consumables, setConsumables] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/packaging-consumables")
      .then((r) => setConsumables(r.data || []))
      .catch(() => setConsumables([]))
      .finally(() => setLoading(false));
  }, []);

  const consumablesById = useMemo(
    () => Object.fromEntries(consumables.map((c) => [c.id, c])),
    [consumables]
  );

  const items = packaging || [];

  const addItem = () => {
    const first = consumables[0];
    if (!first) return;
    const next = [
      ...items,
      {
        consumable_id: first.id,
        quantity_per_unit: 1,
        unit: first.consumption_unit || "unité",
      },
    ];
    onChange(next);
  };

  const updateItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };

  const removeItem = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const handleConsumableChange = (idx, consumableId) => {
    const consumable = consumablesById[consumableId];
    if (!consumable) return;
    updateItem(idx, {
      consumable_id: consumableId,
      unit: consumable.consumption_unit || "unité",
    });
  };

  // Trouve la ligne calculée correspondante dans le breakdown
  const findLineCost = (consumableId) => {
    if (!breakdownPackaging?.lines) return null;
    return breakdownPackaging.lines.find((ln) => ln.consumable_id === consumableId);
  };

  return (
    <section
      className="border border-[#E5E5E0] rounded-md bg-[#FAFAF7] p-4 space-y-3"
      data-testid="product-packaging-section"
    >
      <header className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <Package2 className="w-4 h-4 text-[#1F4E3D]" />
            <h3 className="font-heading text-sm font-semibold text-[#1A1C1A]">
              Consommables emballage
            </h3>
          </div>
          <p className="text-xs text-[#575A57] mt-1 leading-relaxed">
            Barquettes, films, étiquettes utilisés pour conditionner UNE unité finie.
            N'apparaît pas sur les étiquettes RGPD ni en cuisine, sert au calcul du prix de revient complet.
          </p>
        </div>
        {breakdownPackaging && breakdownPackaging.total_cost_chf > 0 && (
          <div className="text-right flex-shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-[#575A57]">Coût emballage</div>
            <div className="font-mono text-base font-semibold text-[#1F4E3D]" data-testid="packaging-total-cost">
              {breakdownPackaging.total_cost_chf.toFixed(3)} <span className="text-xs">CHF</span>
            </div>
            <div className="text-[10px] text-[#A3A6A3]">par unité</div>
          </div>
        )}
      </header>

      {loading ? (
        <div className="text-xs text-[#575A57] py-2">Chargement…</div>
      ) : consumables.length === 0 ? (
        <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded p-3 text-xs text-[#8C6B2F]">
          <Info className="w-3.5 h-3.5 inline mr-1" />
          Aucun consommable configuré. Créez-en d'abord dans <a href="/parametres/emballages" target="_blank" className="underline">Paramètres → Consommables emballage</a>.
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-xs text-[#575A57] mb-2">Aucun consommable lié</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addItem}
            data-testid="add-packaging-item"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter une barquette / film
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-2" data-testid="packaging-items">
            {items.map((item, idx) => {
              const consumable = consumablesById[item.consumable_id];
              const lineCost = findLineCost(item.consumable_id);
              return (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 items-center bg-white border border-[#E5E5E0] rounded p-2"
                  data-testid={`packaging-item-${idx}`}
                >
                  <div className="col-span-5">
                    <Select
                      value={item.consumable_id}
                      onValueChange={(v) => handleConsumableChange(idx, v)}
                    >
                      <SelectTrigger data-testid={`packaging-consumable-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {consumables.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="text-xs text-[#A3A6A3] mr-1.5">
                              [{CONSUMABLE_TYPE_LABELS[c.type] || c.type}]
                            </span>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <NumericInput
                      value={item.quantity_per_unit}
                      onChange={(e) => {
                        const v = e.target.value;
                        updateItem(idx, {
                          quantity_per_unit: v === "" ? 0 : Number(v),
                        });
                      }}
                      data-testid={`packaging-qty-${idx}`}
                      className="text-right font-mono"
                    />
                  </div>
                  <div className="col-span-1 text-xs text-[#575A57] font-mono">
                    {consumable?.consumption_unit || item.unit || "unité"}
                  </div>
                  <div className="col-span-3 text-right text-xs">
                    {lineCost?.line_cost_chf != null ? (
                      <>
                        <span className="text-[#A3A6A3]">≈ </span>
                        <span className="font-mono font-semibold text-[#1F4E3D]">
                          {lineCost.line_cost_chf.toFixed(3)} CHF
                        </span>
                      </>
                    ) : lineCost?.error ? (
                      <span className="text-[#A04E2C] text-[10px]">{lineCost.error}</span>
                    ) : (
                      <span className="text-[#A3A6A3] text-[10px]">—</span>
                    )}
                  </div>
                  <div className="col-span-1 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(idx)}
                      data-testid={`packaging-remove-${idx}`}
                      title="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-[#C05C47]" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addItem}
            data-testid="add-packaging-item"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter un consommable
          </Button>
        </>
      )}
    </section>
  );
}
